import { supabase } from '../../config/supabase';
import { Property } from '../../types/user';
import { updatePropertyStats } from '../stats/propertyStatsUpdater';

export const getProperties = async (): Promise<Property[]> => {
  const { data, error } = await supabase
    .from('properties')
    .select('*');
  
  if (error) throw error;
  
  // Obtener estadísticas desde monthly_stats para cada propiedad
  const propertiesWithStats = await Promise.all(
    data.map(async (property) => {
      const { data: monthlyStats, error: statsError } = await supabase
        .from('monthly_stats')
        .select('*')
        .eq('property_id', property.id);
      
      if (statsError) {
        console.error('Error cargando stats:', statsError);
      }
      
      // Calcular estadísticas desde monthly_stats
      const stats = monthlyStats || [];
      const totalRevenue = stats.reduce((sum, stat) => sum + (stat.revenue || 0), 0);
      const totalCleaningCosts = stats.reduce((sum, stat) => sum + (stat.cleaning_costs || 0), 0);
      const totalInmohostCosts = stats.reduce((sum, stat) => sum + (stat.inmohost_costs || 0), 0);
      const totalProfit = stats.reduce((sum, stat) => sum + (stat.profit || 0), 0);
      const totalReservations = stats.reduce((sum, stat) => sum + (stat.reservations || 0), 0);
      const totalOccupancy = stats.reduce((sum, stat) => sum + (stat.occupancy_rate || 0), 0);
      const avgOccupancy = stats.length > 0 ? Math.round(totalOccupancy / stats.length) : 0;
      const totalNightPrice = stats.reduce((sum, stat) => sum + (stat.average_night_price || 0), 0);
      const avgNightPrice = stats.length > 0 ? Math.round(totalNightPrice / stats.length) : 0;
      const totalStayDuration = stats.reduce((sum, stat) => sum + (stat.average_stay_duration || 0), 0);
      const avgStayDuration = stats.length > 0 ? Math.round(totalStayDuration / stats.length) : 0;
      
      return {
        id: property.id,
        name: property.name,
        location: property.location,
        ownerId: property.owner_id,
        stats: {
          revenue: totalRevenue,
          cleaningCosts: totalCleaningCosts,
          inmohostCosts: totalInmohostCosts,
          profit: totalProfit,
          occupancyRate: avgOccupancy,
          reservations: totalReservations,
          averageNightPrice: avgNightPrice,
          averageStayDuration: avgStayDuration
        },
        created_at: property.created_at
      };
    })
  );
  
  return propertiesWithStats;
};

export const createProperty = async (property: Omit<Property, 'id' | 'created_at'>): Promise<Property> => {
  const { data, error } = await supabase
    .from('properties')
    .insert([{
      name: property.name,
      location: property.location,
      owner_id: property.ownerId,
      stats: {
        revenue: 0,
        cleaning_costs: 0,
        inmohost_costs: 0,
        profit: 0,
        occupancy_rate: 0,
        reservations: 0,
        average_night_price: 0,
        average_stay_duration: 0
      }
    }])
    .select()
    .single();

  if (error) throw error;

  return {
    id: data.id,
    name: data.name,
    location: data.location,
    ownerId: data.owner_id,
    stats: {
      revenue: Number(data.stats?.revenue || 0),
      cleaningCosts: Number(data.stats?.cleaning_costs || 0),
      inmohostCosts: Number(data.stats?.inmohost_costs || 0),
      profit: Number(data.stats?.profit || 0),
      occupancyRate: Number(data.stats?.occupancy_rate || 0),
      reservations: Number(data.stats?.reservations || 0),
      averageNightPrice: Number(data.stats?.average_night_price || 0),
      averageStayDuration: Number(data.stats?.average_stay_duration || 0)
    },
    created_at: data.created_at
  };
};

export const updateProperty = async (property: Property): Promise<void> => {
  const { error } = await supabase
    .from('properties')
    .update({
      name: property.name,
      location: property.location,
      owner_id: property.ownerId,
      stats: {
        revenue: property.stats.revenue,
        cleaning_costs: property.stats.cleaningCosts,
        inmohost_costs: property.stats.inmohostCosts,
        profit: property.stats.profit,
        occupancy_rate: property.stats.occupancyRate,
        reservations: property.stats.reservations,
        average_night_price: property.stats.averageNightPrice,
        average_stay_duration: property.stats.averageStayDuration
      }
    })
    .eq('id', property.id);

  if (error) throw error;
};

export const deleteProperty = async (id: string): Promise<void> => {
  const { error } = await supabase
    .from('properties')
    .delete()
    .eq('id', id);

  if (error) throw error;
};
